import React, { useState, useMemo } from 'react';
import { Button, Space, Input, Form, Row, Col, Tooltip, Typography, notification, Switch } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import CustomSelect from '../../components/CustomSelect';
import NormalFormLabel from '../../components/NormalFormLabel';
import {
  deleteMemberRequest,
  getListAllPartnersRequest,
  getListAllRolesRequest,
  getListMembersRequest,
  getStatusAccountRequest,
  updateMemberRequest,
} from '../../services/memberRequest';
import { useMutation, useQuery } from '@tanstack/react-query';
import ThemNguoiDung from './components/themNguoiDung';
import SuaNguoiDung from './components/suaNguoiDung';
import { checkPermission } from '../../utils/checkPermission';
import { newObjectKeys } from '../../utils/utility';
import { TableCustomV2 } from '../../components/TableCustomV2';
import { CREATE_MEMBER, DELETE_MEMBER, GET_LIST_MEMBERS, UPDATE_MEMBER } from '../../constants/apiEndPoint';
import { isEqual } from 'lodash';

const QuanTriNguoiDung = () => {
  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [updateAccountOpen, setUpdateAccountOpen] = useState(false);
  const [memberInfo, setMemberInfo] = useState(null);
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  const {
    data: dataMembers,
    isPending,
    refetch: refetchDataMembers,
  } = useQuery({
    queryKey: ['dataMembers', params],
    queryFn: () => getListMembersRequest(params),
    enabled: checkPermission(GET_LIST_MEMBERS, 'Xem danh sách'),
  });

  // get data partners
  const { data: dataAllPartners } = useQuery({
    queryKey: ['dataAllPartners'],
    queryFn: () => getListAllPartnersRequest(),
  });

  // convert list partners options
  const dataAllPartnersOption = useMemo(() => {
    const options =
      dataAllPartners?.map((item) => {
        return { label: item?.name, value: item?.id };
      }) || [];
    return options;
  }, [dataAllPartners]);

  // get data roles
  const { data: dataAllRoles } = useQuery({
    queryKey: ['dataAllRoles'],
    queryFn: () => getListAllRolesRequest(),
  });

  // convert list roles option filter
  const dataAllRolesOption = useMemo(() => {
    const options =
      dataAllRoles?.map((item) => {
        return { label: item?.name, value: item?.value };
      }) || [];
    return options;
  }, [dataAllRoles]);

  // get data status account
  const { data: dataStatusAccount } = useQuery({
    queryKey: ['dataStatusAccount'],
    queryFn: () => getStatusAccountRequest(),
  });

  // convert list status account option filter
  const dataStatusAccountOption = useMemo(() => {
    const options =
      dataStatusAccount?.map((item) => {
        return { label: item?.name, value: item?.value };
      }) || [];
    return options;
  }, [dataStatusAccount]);

  const { mutate: mutateUpdateMember, isPending: isLoadingUpdateMember } = useMutation({
    mutationFn: updateMemberRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Cập nhật trạng thái người dùng thành công!',
        });
        refetchDataMembers();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const { mutate: mutateDeleteMember } = useMutation({
    mutationFn: deleteMemberRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Xóa tài khoản người dùng thành công!',
        });
        refetchDataMembers();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '60px',
      fixed: 'left',
    },
    {
      title: 'Họ tên',
      dataIndex: 'fullname',
      render: (fullname) => (
        <Typography.Text className="text-[#1677FF] hover:cursor-pointer">{fullname}</Typography.Text>
      ),
      key: 'fullname',
      width: '200px',
      fixed: 'left',
    },
    {
      title: 'Đối tác',
      dataIndex: 'partner_name',
      key: 'partner_id',
      width: '200px',
    },
    {
      title: 'Nhóm quyền',
      dataIndex: 'role_name',
      key: 'role',
      width: '200px',
    },
    {
      title: 'Tên đăng nhập',
      dataIndex: 'username',
      key: 'username',
      width: '150px',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '120px',
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phone',
      key: 'phone',
      width: '120px',
    },
    {
      title: 'Cập nhật lần cuối',
      key: 'updated_by',
      width: '170px',
      render: (_, { updated_by_name, updated_at }) => (
        <Space direction="vertical">
          <Typography.Text>{updated_by_name}</Typography.Text>
          <Typography.Text>{updated_at}</Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      render: (_, record) => (
        <Switch
          onClick={(value) => {
            if (checkPermission(UPDATE_MEMBER, 'Chỉnh sửa') && !isLoadingUpdateMember) {
              mutateUpdateMember({
                id: record?.id,
                fullname: record?.fullname,
                type: record?.type,
                partner_id: record?.partner_id,
                role: record?.role,
                username: record?.username,
                change_password: false,
                email: record?.email,
                phone: record?.phone,
                is_active: record?.is_active ? 0 : 1,
              });
            }
          }}
          value={!!record?.is_active}
        />
      ),
      width: '100px',
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {checkPermission(UPDATE_MEMBER, 'Chỉnh sửa') && (
            <Tooltip title="Chỉnh sửa người dùng">
              <EditOutlined
                className="text-base"
                onClick={() => {
                  setMemberInfo(record);
                  setUpdateAccountOpen(true);
                }}
              />
            </Tooltip>
          )}
          {checkPermission(DELETE_MEMBER, 'Xóa') && record?.status !== 2 && (
            <Tooltip title="Xóa tài khoản người dùng">
              <DeleteOutlined className="text-base" onClick={() => mutateDeleteMember({ id: record?.id })} />
            </Tooltip>
          )}
        </Space>
      ),
      width: '90px',
      fixed: 'right',
      hidden: !checkPermission(DELETE_MEMBER, 'Xóa') && !checkPermission('system_manager', 'member', 'delete'),
    },
  ];

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: 20 };

    if (values?.username) {
      Object.assign(temp, { username: values?.username });
    }

    if (values?.email) {
      Object.assign(temp, { email: values?.email });
    }

    if (values?.phone) {
      Object.assign(temp, { phone: values?.phone });
    }

    if (values?.partner_id >= 0) {
      Object.assign(temp, { partner_id: values?.partner_id });
    }

    if (values?.role_id >= 0) {
      Object.assign(temp, { role_id: values?.role_id });
    }

    if (values?.status >= 0) {
      Object.assign(temp, { status: values?.status });
    }

    if (isEqual(temp, params)) {
      refetchDataMembers();
    } else {
      setParams(temp);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="font-semibold text-2xl font-roboto">Danh sách người dùng</h1>
        {checkPermission(CREATE_MEMBER, 'Tạo mới') && (
          <Button type="primary" onClick={() => setCreateAccountOpen(true)}>
            <PlusOutlined /> Thêm người dùng
          </Button>
        )}
      </div>

      <ThemNguoiDung
        open={createAccountOpen}
        setOpen={setCreateAccountOpen}
        refetchDataMembers={refetchDataMembers}
        dataPartnersOption={
          dataAllPartners
            ?.filter((item) => item.status === 1)
            ?.map((item) => {
              return { label: item?.name, value: item?.value };
            }) || []
        }
        dataPermissionGroupsOption={
          dataAllRoles
            ?.filter((item) => item.status === 1)
            ?.map((item) => {
              return { label: item?.name, value: item?.value };
            }) || []
        }
        dataStatusAccountOption={dataStatusAccountOption}
      />

      <SuaNguoiDung
        open={updateAccountOpen}
        setOpen={setUpdateAccountOpen}
        refetchDataMembers={refetchDataMembers}
        dataPartnersOption={
          dataAllPartners
            ?.filter((item) => item.status === 1)
            ?.map((item) => {
              return { label: item?.name, value: item?.value };
            }) || []
        }
        dataPermissionGroupsOption={
          dataAllRoles
            ?.filter((item) => item.status === 1)
            ?.map((item) => {
              return { label: item?.name, value: item?.value };
            }) || []
        }
        dataStatusAccountOption={dataStatusAccountOption}
        memberInfo={memberInfo}
        setMemberInfo={setMemberInfo}
      />

      {checkPermission(GET_LIST_MEMBERS, 'Xem danh sách') && (
        <>
          <Form layout="vertical" name="search-account" form={formSearch} onFinish={handleSubmitForm}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Tên tài khoản" />} name="username">
                  <Input allowClear placeholder="Nhập tên tài khoản..." />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Email" />} name="email">
                  <Input allowClear placeholder="Nhập email..." />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Số điện thoại" />} name="phone">
                  <Input allowClear placeholder="Nhập số điện thoại..." />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Đối tác" />} name="partner_id" initialValue={-1}>
                  <CustomSelect
                    placeholder="Đối tác"
                    options={[{ label: 'Tất cả', value: -1 }, ...dataAllPartnersOption]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Nhóm quyền" />} name="role_id" initialValue={-1}>
                  <CustomSelect
                    placeholder="Nhóm quyền"
                    options={[{ label: 'Tất cả', value: -1 }, ...dataAllRolesOption]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Trạng thái" />} name="status" initialValue={-1}>
                  <CustomSelect
                    placeholder="Trạng thái"
                    options={[{ value: -1, label: 'Tất cả' }, ...dataStatusAccountOption]}
                  />
                </Form.Item>
              </Col>

              <Col className="flex items-end">
                <Form.Item>
                  <Button type="primary" className=" " htmlType="submit">
                    <SearchOutlined /> Tìm kiếm
                  </Button>
                  {params &&
                    Object.values(
                      newObjectKeys(params, ['username', 'email', 'phone', 'partner_id', 'role_id', 'status'])
                    ).some((value) => value || value === 0) && (
                      <Button
                        htmlType="button"
                        className="ml-3"
                        onClick={() => {
                          formSearch.resetFields();
                          setParams({ page: 1, limit: params.limit });
                        }}
                      >
                        <DeleteOutlined />
                        Xóa lọc
                      </Button>
                    )}
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <TableCustomV2
            isLoading={isPending}
            columns={columns}
            dataSource={dataMembers?.data}
            totalItem={dataMembers?.totalItem}
            params={params}
            setParams={setParams}
          />
        </>
      )}
    </div>
  );
};

export default QuanTriNguoiDung;
